import React, { useState } from 'react';
import {
  Edit2,
  HelpCircle,
  Info,
  ArrowRight,
  Trash2,
  Globe,
  ChevronUp,
  ChevronDown,
  Shuffle,
  Settings,
  Copy,
  FileJson,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { InstanceSummary } from './instance-summary';
import { SplitConfigDialog } from './split-config-dialog';
import { toast } from 'sonner';

// Quick matcher patterns
const QUICK_PATTERNS = {
  ALL: '.*',
  STEP1: '/step1',
  STEP2: '/step2',
  CHECKOUT: '/checkout',
  THANKYOU: '/thankyou',
} as const;

// Pattern examples for help UI
const PATTERN_EXAMPLES = [
  {
    pattern: '.*',
    desc: 'Catch-all: Match all routes',
    matches: ['/', '/checkout', '/step1'],
  },
  {
    pattern: '/checkout',
    desc: 'Prefix: /checkout and subpaths',
    matches: ['/checkout', '/checkout/step1'],
  },
  {
    pattern: '/step2$',
    desc: 'Exact: Only /step2 (no subpaths)',
    matches: ['/step2'],
  },
  {
    pattern: '^/checkout.*',
    desc: 'Regex: Paths starting with /checkout',
    matches: ['/checkout', '/checkout/step1'],
  },
  {
    pattern: '^/(step1|step2)',
    desc: 'Regex: /step1 or /step2',
    matches: ['/step1', '/step2'],
  },
];

interface RouteItemProps {
  route: any; // The route object (not instance)
  isPermanentCDN: boolean;
  canDelete?: boolean;
  canRemoveInstance?: boolean; // Instance can be removed (orphan)
  routeIndex?: number;
  totalRoutes?: number;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  versions: string[];
  configs: string[];
  matrix: Record<string, Record<string, any[]>>;
  availableInstances?: any[]; // For split config dialog
  onEdit: (route: any) => void;
  onSave: (route: any) => void;
  onCancel: () => void;
  onSwitch: (currentRoute: any, targetInstance: any) => void;
  onDelete: (route: any) => void;
  onRemoveInstance?: (instanceId: string) => void; // Remove orphan instance
  onCommitToProduction?: (instanceId: string) => void; // Promote preview to production
  onMovePriority?: (route: any, direction: 'up' | 'down') => void;
  onEditConfig?: (route: any) => void;
  onConfigureSplit?: (route: any, splitConfig: any) => void; // Configure split testing
  isEditing: boolean;
  editMatcher: string;
  editExcluder: string;
  setEditMatcher: (value: string) => void;
  setEditExcluder: (value: string) => void;
}

/**
 * Route Item Component
 *
 * Displays a single route with edit mode and switch matrix
 */
export function RouteItem({
  route,
  isPermanentCDN,
  canDelete = true,
  canRemoveInstance = false,
  routeIndex,
  totalRoutes,
  canMoveUp = false,
  canMoveDown = false,
  versions,
  configs,
  matrix,
  availableInstances = [],
  onEdit,
  onSave,
  onCancel,
  onSwitch,
  onDelete,
  onRemoveInstance,
  onCommitToProduction,
  onMovePriority,
  onEditConfig,
  onConfigureSplit,
  isEditing,
  editMatcher,
  editExcluder,
  setEditMatcher,
  setEditExcluder,
}: RouteItemProps) {
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const showPriorityIndicator = totalRoutes && totalRoutes > 1;

  // Copy route ID to clipboard
  const handleCopyRouteId = () => {
    const routeId = route.routeId || route.id;
    navigator.clipboard.writeText(routeId);
    toast.success('Route ID copied to clipboard');
  };

  // Copy entire route config as JSON to clipboard
  const handleCopyRouteConfig = () => {
    const routeConfig = JSON.stringify(route, null, 2);
    navigator.clipboard.writeText(routeConfig);
    toast.success(
      'Route config copied to clipboard (Note: htmlInjections/metadata may be incomplete in this view)',
    );
  };

  return (
    <div className="group relative">
      {/* Priority Up/Down Controls - Fixed Position Left */}
      {showPriorityIndicator && !isEditing && !isPermanentCDN && (
        <div className="absolute left-0 top-1/2 flex -translate-y-1/2 flex-col gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          {canMoveUp && onMovePriority && (
            <button
              onClick={() => onMovePriority(route, 'up')}
              className="bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground flex h-5 w-5 items-center justify-center rounded-sm shadow-sm transition-colors"
              title="Increase priority (check this route earlier)"
            >
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
          )}
          {canMoveDown && onMovePriority && (
            <button
              onClick={() => onMovePriority(route, 'down')}
              className="bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground flex h-5 w-5 items-center justify-center rounded-sm shadow-sm transition-colors"
              title="Decrease priority (check this route later)"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      <div
        className={`relative rounded-lg border p-2.5 shadow-sm transition-all duration-200 ${showPriorityIndicator ? 'group-hover:ml-6' : ''} ${isPermanentCDN ? 'border-purple-200 bg-purple-50/50 dark:border-purple-900/30 dark:bg-purple-950/20' : 'border-border bg-card group-hover:border-border/80 group-hover:shadow-md'}`}
      >
        {/* Priority Number - Top Left Corner (moves with card) */}
        {showPriorityIndicator && (
          <div
            className={`border-background absolute -left-3 -top-2 flex h-6 w-6 shrink-0 flex-col items-center justify-center rounded-full border-2 text-[10px] font-bold shadow-sm ${
              routeIndex === 1
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted text-muted-foreground'
            }`}
            title={`Priority rank: ${routeIndex} (checked ${routeIndex === 1 ? '1st - HIGHEST PRIORITY' : routeIndex === 2 ? '2nd' : routeIndex === 3 ? '3rd' : `${routeIndex}th`}). Lower numbers = higher priority.`}
          >
            {routeIndex === 1 && <div className="text-[6px] leading-none">↑</div>}
            <div className="leading-none">{routeIndex}</div>
          </div>
        )}

        {/* Action Buttons - Top Right Corner */}
        {!isEditing && !isPermanentCDN && (
          <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            {onConfigureSplit && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowSplitDialog(true)}
                className="h-6 gap-1 px-2 text-xs"
                title="Configure split testing"
              >
                <Shuffle className="h-3 w-3" />
                <span>Split</span>
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEdit(route)}
              className="h-6 gap-1 px-2 text-xs"
            >
              <Edit2 className="h-3 w-3" />
              <span>Edit Route</span>
            </Button>
            {canDelete && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDelete(route)}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive h-6 gap-1 px-2 text-xs"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}

        {/* Permanent Route Indicator */}
        {isPermanentCDN && !isEditing && (
          <div className="absolute right-2 top-2">
            <Badge
              variant="outline"
              className="h-6 border-purple-300 bg-purple-50 text-xs font-medium text-purple-700 dark:border-purple-800 dark:bg-purple-950/50 dark:text-purple-300"
            >
              Permanent
            </Badge>
          </div>
        )}

        {/* Content */}
        {isEditing ? (
          <div className="space-y-3">
            {/* Edit Mode Header */}
            <div className="flex items-center justify-between">
              <div className="text-foreground text-sm font-semibold">Route Configuration</div>
              <div className="flex gap-1.5">
                <Button size="sm" onClick={() => onSave(route)} className="h-6 px-2.5 text-xs">
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={onCancel} className="h-6 px-2.5 text-xs">
                  Cancel
                </Button>
              </div>
            </div>

            {/* Edit Form */}
            <div className="border-border bg-muted/30 space-y-2.5 rounded-md border p-2.5 shadow-sm">
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-muted-foreground text-xs font-medium">Matcher Pattern</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-5 gap-1 px-2 text-xs">
                        <Info className="h-3 w-3" />
                        Examples
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-96" align="end">
                      <div className="space-y-3">
                        <div>
                          <h4 className="mb-2 text-sm font-semibold">Pattern Examples</h4>
                          <p className="text-muted-foreground mb-3 text-xs">
                            Using path-to-regexp native format
                          </p>
                        </div>
                        <div className="space-y-2">
                          {PATTERN_EXAMPLES.map((example) => (
                            <div
                              key={example.pattern}
                              className="bg-muted/30 hover:bg-muted/50 cursor-pointer rounded-md p-2 transition-colors"
                              onClick={() => {
                                setEditMatcher(example.pattern);
                              }}
                            >
                              <div className="mb-1 flex items-center gap-2">
                                <code className="bg-background rounded px-1.5 py-0.5 font-mono text-xs">
                                  {example.pattern}
                                </code>
                              </div>
                              <p className="text-muted-foreground mb-1 text-xs">{example.desc}</p>
                              <div className="flex flex-wrap gap-1">
                                {example.matches.map((match) => (
                                  <code
                                    key={match}
                                    className="rounded bg-green-500/10 px-1 text-xs text-green-700"
                                  >
                                    {match}
                                  </code>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="border-t pt-2">
                          <p className="text-muted-foreground text-xs">
                            <strong>Tip:</strong> Use <code className="bg-background rounded px-1">$</code>{' '}
                            for exact match, <code className="bg-background rounded px-1">^</code> for regex
                          </p>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <input
                  type="text"
                  value={editMatcher}
                  onChange={(e) => setEditMatcher(e.target.value)}
                  className="bg-background border-input ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-8 w-full rounded-md border px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  placeholder="e.g., .* or /step2"
                />
                <div className="flex flex-wrap gap-1">
                  {[
                    { label: 'All', value: QUICK_PATTERNS.ALL },
                    { label: 'Step1', value: QUICK_PATTERNS.STEP1 },
                    { label: 'Step2', value: QUICK_PATTERNS.STEP2 },
                    { label: 'Checkout', value: QUICK_PATTERNS.CHECKOUT },
                  ].map((quick) => (
                    <Badge
                      key={quick.value}
                      variant="outline"
                      className="hover:bg-primary hover:text-primary-foreground cursor-pointer text-xs"
                      onClick={() => setEditMatcher(quick.value)}
                    >
                      {quick.label}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-muted-foreground text-xs font-medium">Excluder (optional)</label>
                <input
                  type="text"
                  value={editExcluder}
                  onChange={(e) => setEditExcluder(e.target.value)}
                  className="bg-background border-input ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-8 w-full rounded-md border px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  placeholder="e.g., /admin"
                />
                <p className="text-muted-foreground text-xs">Removes matches from matcher pattern</p>
              </div>
            </div>
            {/* Switch Matrix */}
            {!isPermanentCDN && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Separator className="flex-1" />
                  <span className="text-muted-foreground text-xs font-medium">Switch Version / Config</span>
                  <Separator className="flex-1" />
                </div>
                <div className="space-y-1">
                  {versions.map((version) => (
                    <div key={version} className="flex items-center gap-2.5">
                      <div className="text-muted-foreground w-14 shrink-0 text-xs font-medium">
                        v{version}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {configs.map((config) => {
                          const targetInstances = matrix[version][config] || [];

                          // Skip if no instances for this config
                          if (targetInstances.length === 0) {
                            return null;
                          }

                          // Check if this specific instance ID is in the array
                          const matchingInstance = targetInstances.find(
                            (inst) => inst.instanceId === route.instanceId,
                          );
                          const isCurrentInstance = !!matchingInstance;

                          // Use the first instance for switching (or the matching one if current)
                          const instanceToUse = matchingInstance || targetInstances[0];

                          return (
                            <Badge
                              key={config}
                              variant={isCurrentInstance ? 'default' : 'outline'}
                              className={`cursor-pointer text-xs transition-all ${
                                isCurrentInstance
                                  ? 'border-green-600 bg-green-600 hover:bg-green-700'
                                  : 'hover:border-primary hover:bg-primary/10'
                              }`}
                              onClick={() => onSwitch(route, instanceToUse)}
                              title={`Switch to: ${instanceToUse.instanceId}${targetInstances.length > 1 ? ` (+${targetInstances.length - 1} more)` : ''}`}
                            >
                              {config}
                              {isCurrentInstance && ' ✓'}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : route.splitConfig ? (
          <div className="space-y-2.5">
            {/* Route ID and Debug Actions */}
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="hover:bg-muted cursor-pointer gap-1 font-mono text-[10px]"
                onClick={handleCopyRouteId}
                title="Click to copy route ID"
              >
                <Copy className="h-2.5 w-2.5" />
                {route.routeId || route.id}
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCopyRouteConfig}
                className="h-5 gap-1 px-1.5 text-[10px]"
                title="Copy entire route config as JSON"
              >
                <FileJson className="h-3 w-3" />
                Copy JSON
              </Button>
            </div>

            {/* Matcher/Excluder Pattern with Split Type Inline */}
            <div className="flex items-center gap-2">
              {/* Path Remapping Flow: External → Matcher → Internal */}
              {route.pathRemap && (
                <>
                  <Badge
                    variant="outline"
                    className="h-5 border-green-300 bg-green-50 text-[10px] font-medium text-green-700 dark:border-green-700 dark:bg-green-950/30 dark:text-green-400"
                  >
                    <code className="font-mono">{route.pathRemap.to}</code>
                  </Badge>
                  <ArrowRight className="text-muted-foreground/40 h-3 w-3 shrink-0" />
                </>
              )}

              <div className="flex items-center gap-1.5">
                <code className="border-border/40 bg-muted/40 text-muted-foreground/80 rounded border px-1.5 py-0.5 text-[11px]">
                  {route.matcher || '.*'}
                </code>
                {route.excluder && (
                  <>
                    <span className="text-muted-foreground/50 text-[10px]">!</span>
                    <code className="border-destructive/20 bg-destructive/5 text-destructive/70 rounded border px-1.5 py-0.5 text-[11px]">
                      {route.excluder}
                    </code>
                  </>
                )}
              </div>
              <ArrowRight className="text-muted-foreground/40 h-3 w-3 shrink-0" />

              {/* Internal Path (what plugin receives) */}
              {route.pathRemap && (
                <>
                  <Badge
                    variant="outline"
                    className="h-5 border-blue-300 bg-blue-50 text-[10px] font-medium text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-400"
                  >
                    <code className="font-mono">{route.pathRemap.from}</code>
                  </Badge>
                  <ArrowRight className="text-muted-foreground/40 h-3 w-3 shrink-0" />
                </>
              )}

              <Badge variant="secondary" className="h-5 gap-1 text-[10px] font-medium">
                {route.splitConfig.type === 'weighted' ? (
                  <>
                    <Shuffle className="h-3 w-3" /> {route.splitConfig.instances.length}-way
                  </>
                ) : (
                  <>
                    <Globe className="h-3 w-3" /> Geo
                  </>
                )}
              </Badge>
              <span className="text-muted-foreground text-[9px]">
                • sticky: {Math.floor(route.splitConfig.stickyDuration / 86400)}d
              </span>
            </div>

            {/* Split Instances - Compact Grid */}
            <div className="grid gap-1.5 pl-0">
              {route.splitConfig.instances.map((split: any, idx: number) => {
                // Use enriched instance data from backend (configName, version)
                // Fallback to availableInstances lookup if needed
                const instanceDetails = availableInstances.find(
                  (inst: any) => inst.id === split.instanceId || inst.instanceId === split.instanceId,
                );

                // Create a fake route object for this instance to enable config editing
                const instanceRoute = {
                  ...route,
                  instanceId: split.instanceId,
                  splitConfig: undefined,
                  instance: instanceDetails,
                };

                return (
                  <div
                    key={idx}
                    className="group/split hover:bg-muted/50 bg-muted/20 hover:border-border flex items-center justify-between gap-2 rounded border border-transparent px-2 py-1.5 text-xs transition-colors"
                  >
                    <div className="flex flex-1 items-center gap-2">
                      <div className="bg-primary/10 text-primary flex h-5 w-5 shrink-0 items-center justify-center rounded text-[9px] font-bold">
                        {idx + 1}
                      </div>
                      <Badge variant="outline" className="text-[10px] font-medium">
                        {split.configName ||
                          instanceDetails?.config?.configName ||
                          instanceDetails?.configName ||
                          'unknown'}
                      </Badge>
                      <span className="text-muted-foreground text-[10px]">
                        v{split.version || instanceDetails?.version || '?'}
                      </span>
                      {instanceDetails?.isPreview && (
                        <Badge variant="secondary" className="text-[10px]">
                          🧪
                        </Badge>
                      )}
                      <Separator orientation="vertical" className="h-3" />
                      {route.splitConfig.type === 'weighted' && split.weight !== undefined && (
                        <code className="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-[10px] font-semibold">
                          {split.weight}%
                        </code>
                      )}
                      {route.splitConfig.type === 'geo' && split.regions && (
                        <code className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                          {split.regions.join(', ')}
                        </code>
                      )}
                    </div>
                    {onEditConfig && instanceDetails && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onEditConfig(instanceRoute)}
                        className="h-5 gap-1 px-1.5 text-[10px] opacity-0 transition-opacity group-hover/split:opacity-100"
                      >
                        <Settings className="h-3 w-3" />
                        <span>Edit</span>
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            {/* Route ID and Debug Actions */}
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="hover:bg-muted cursor-pointer gap-1 font-mono text-[10px]"
                onClick={handleCopyRouteId}
                title="Click to copy route ID"
              >
                <Copy className="h-2.5 w-2.5" />
                {route.routeId || route.id}
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCopyRouteConfig}
                className="h-5 gap-1 px-1.5 text-[10px]"
                title="Copy entire route config as JSON"
              >
                <FileJson className="h-3 w-3" />
                Copy JSON
              </Button>
            </div>

            <InstanceSummary
              instance={{
                ...route.instance,
                instanceId: route.instanceId,
                matcher: route.matcher,
                excluder: route.excluder,
              }}
              pathRemap={route.pathRemap}
              onEditConfig={onEditConfig ? () => onEditConfig(route) : undefined}
              onRemoveInstance={
                canRemoveInstance && onRemoveInstance ? () => onRemoveInstance(route.instanceId) : undefined
              }
              onCommit={onCommitToProduction ? () => onCommitToProduction(route.instanceId) : undefined}
            />
          </div>
        )}
      </div>

      {/* Split Config Dialog */}
      {onConfigureSplit && (
        <SplitConfigDialog
          open={showSplitDialog}
          onOpenChange={setShowSplitDialog}
          route={{
            id: route.routeId || route.id,
            matcher: route.matcher || '.*',
            hostname: route.hostname || '',
            basePath: route.basePath || '/',
          }}
          availableInstances={availableInstances}
          existingConfig={route.splitConfig}
          onSave={(splitConfig) => onConfigureSplit(route, splitConfig)}
        />
      )}
    </div>
  );
}
